import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Textarea,
  Button,
  Spinner,
  Checkbox,
} from '@material-tailwind/react';
import {
  EyeIcon,
  EyeSlashIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  PhoneIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

const fmtNum = (v) => (v != null && !isNaN(v) ? new Intl.NumberFormat('en-US').format(Math.round(v)) : '0');
const fmtPct = (v) => (v != null && !isNaN(v) ? `${(v * 100).toFixed(2)}%` : '0%');


// defaultHidden charts render only when their id IS in hiddenCharts (inverse
// semantics, matching the report renderer) — used for the split market
// positioning variants so existing drafts are unaffected.
const CHART_CONFIG = [
  { id: 'performance-hero', prop: 'performanceHero', label: 'Performance Comparison' },
  { id: 'sales-trajectory-chart', prop: 'salesTrajectory', label: 'Sales Trajectory' },
  { id: 'price-history-chart', prop: 'priceHistory', label: 'Price History' },
  { id: 'market-position-chart', prop: 'marketPosition', label: 'Market Position' },
  { id: 'market-position-onmarket-chart', prop: 'marketPositionOnMarket', label: 'Market Position (On Market only)', defaultHidden: true },
  { id: 'market-position-sales-chart', prop: 'marketPositionSales', label: 'Market Position (Sold only)', defaultHidden: true },
  { id: 'market-position-time-chart', prop: 'marketPositionTime', label: 'Pricing Recency' },
  { id: 'market-position-time-onmarket-chart', prop: 'marketPositionTimeOnMarket', label: 'Pricing Recency (On Market only)', defaultHidden: true },
  { id: 'market-position-time-sales-chart', prop: 'marketPositionTimeSales', label: 'Pricing Recency (Sold only)', defaultHidden: true },
  { id: 'views-impressions-chart', prop: 'viewsAndImpressions', label: 'Views & Impressions' },
  { id: 'cumulative-chart', prop: 'cumulativeExposure', label: 'Cumulative Exposure' },
  { id: 'momentum-chart', prop: 'weeklyMomentum', label: 'Weekly Momentum' },
  { id: 'ctr-chart', prop: 'ctrTrend', label: 'Click-Through Rate' },
  { id: 'engagement-chart', prop: 'leadActivity', label: 'Lead Activity' },
  { id: 'featured-impact-chart', prop: 'featuredImpact', label: 'Featured Impact' },
];

const KPI_CONFIG = [
  { id: 'totalViews', icon: ChartBarIcon, label: 'Views/day', getValue: (c) => (c.dailyAvgViews ?? 0).toFixed(1), color: 'bg-blue-50 text-blue-600' },
  { id: 'totalImpressions', icon: ChartBarIcon, label: 'Impr./day', getValue: (c) => fmtNum(c.dailyAvgImpressions ?? (c.totalImpressions || 0) / Math.max(c.daysAging || 1, 1)), color: 'bg-purple-50 text-purple-600' },
  { id: 'recentTrend', icon: ArrowTrendingUpIcon, label: 'Recent 4wk Views/day',
    getValue: (c) => {
      if (c.weeksTracked >= 4 && c.last4WeeksAvgViews > 0) return (c.last4WeeksAvgViews / 7).toFixed(1);
      return '—';
    },
    color: 'bg-cyan-50 text-cyan-600',
    show: (c) => c.weeksTracked >= 4 && c.last4WeeksAvgViews > 0,
  },
  { id: 'overallCtr', icon: ArrowTrendingUpIcon, label: 'Overall CTR', getValue: (c) => fmtPct(c.overallCtr), color: 'bg-amber-50 text-amber-600' },
  { id: 'daysAging', icon: ClockIcon, label: 'Days on Market', getValue: (c) => c.daysAging ?? 0, color: 'bg-gray-50 text-gray-600' },
  { id: 'totalWatchlists', icon: ChartBarIcon, label: 'Watchlists/day', getValue: (c) => c.daysAging > 0 ? (c.totalWatchlists / c.daysAging).toFixed(2) : '0', color: 'bg-gray-50 text-gray-600' },
];

const CALLOUT_CONFIG = [
  { id: 'viewsTrend', label: 'Trend', show: () => true },
  { id: 'peakViews', label: 'Peak Views', show: (c) => c.peakViewsCount != null },
  { id: 'featuredImpact', label: 'Featured Lift', show: (c) => c.featuredLiftPct != null },
];

function ListingPerformanceSection({
  content,
  hiddenCharts = [],
  onToggleChart,
  onSave,
  onReset,
  onToggleKpi,
  onSaveCustomLeads,
  isSaving,
}) {
  const [editedCommentary, setEditedCommentary] = useState('');
  const [showLeadsEditor, setShowLeadsEditor] = useState(false);
  const [customLeadsForm, setCustomLeadsForm] = useState({ calls: 0, emails: 0, texts: 0, other: 0, note: '' });
  const [hasChanges, setHasChanges] = useState(false);

  const hiddenKpis = content?.hiddenKpis || [];

  useEffect(() => {
    if (content) {
      setEditedCommentary(content.commentary ?? '');
      setHasChanges(false);
    }
  }, [content?.commentary]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!content) return;
    setHasChanges(editedCommentary !== (content.commentary ?? ''));
  }, [editedCommentary, content?.commentary]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    if (onSave) onSave({ commentary: editedCommentary });
  };

  const handleReset = () => {
    setEditedCommentary(content?.commentary ?? '');
    setHasChanges(false);
    if (onReset) onReset();
  };

  const handleKpiToggle = (kpiId) => {
    if (!onToggleKpi) return;
    const newHidden = hiddenKpis.includes(kpiId)
      ? hiddenKpis.filter((id) => id !== kpiId)
      : [...hiddenKpis, kpiId];
    onToggleKpi(newHidden);
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No listing performance data available</Typography>
      </div>
    );
  }

  const trendColor = (content.viewsTrendPct ?? 0) > 0 ? 'text-green-600 bg-green-50' : (content.viewsTrendPct ?? 0) < -10 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-50';
  const isKpiHidden = (id) => hiddenKpis.includes(id);

  const KpiToggle = ({ id }) => (
    <button
      onClick={(e) => { e.stopPropagation(); handleKpiToggle(id); }}
      className="p-0.5 rounded hover:bg-gray-200 transition-colors"
      title={isKpiHidden(id) ? 'Show in report' : 'Hide from report'}
    >
      {isKpiHidden(id) ? (
        <EyeSlashIcon className="h-3.5 w-3.5 text-gray-400" />
      ) : (
        <EyeIcon className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* KPI Summary */}
      <Card className="border border-blue-200">
        <CardBody className="p-3">
          <Typography variant="small" className="font-medium text-xs text-blue-800 mb-2">
            Listing Performance Summary
          </Typography>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {KPI_CONFIG.filter((kpi) => !kpi.show || kpi.show(content)).map((kpi) => (
              <div
                key={kpi.id}
                className={`flex items-center gap-2 p-2 rounded-lg border transition-opacity ${isKpiHidden(kpi.id) ? 'opacity-40 bg-gray-50' : 'bg-white'}`}
              >
                <div className={`p-1.5 rounded-lg ${kpi.color}`}>
                  <kpi.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <Typography variant="small" color="gray" className="text-xs">{kpi.label}</Typography>
                  <Typography variant="small" className="font-bold">{kpi.getValue(content)}</Typography>
                </div>
                {onToggleKpi && <KpiToggle id={kpi.id} />}
              </div>
            ))}
            {/* Total Leads card with edit */}
            {(() => {
              const controllerCalls = content.totalCalls ?? 0;
              const controllerEmails = content.totalEmails ?? 0;
              const controllerTexts = content.totalTexts ?? 0;
              const cl = content.customLeads || {};
              const totalLeads = controllerCalls + controllerEmails + controllerTexts + (cl.calls || 0) + (cl.emails || 0) + (cl.texts || 0) + (cl.other || 0);
              const hasCustom = cl.calls || cl.emails || cl.texts || cl.other;
              const customTotal = (cl.calls || 0) + (cl.emails || 0) + (cl.texts || 0) + (cl.other || 0);

              return (
                <div className={`flex items-center gap-2 p-2 rounded-lg border transition-opacity col-span-2 lg:col-span-3 ${isKpiHidden('totalLeads') ? 'opacity-40 bg-gray-50' : 'bg-white'}`}>
                  <div className="p-1.5 rounded-lg bg-green-50 text-green-600">
                    <PhoneIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <Typography variant="small" color="gray" className="text-xs">Total Leads</Typography>
                      {onSaveCustomLeads && (
                        <button
                          onClick={() => {
                            setCustomLeadsForm({
                              calls: cl.calls || 0,
                              emails: cl.emails || 0,
                              texts: cl.texts || 0,
                              other: cl.other || 0,
                              note: cl.note || '',
                            });
                            setShowLeadsEditor(!showLeadsEditor);
                          }}
                          className="p-0.5 rounded hover:bg-gray-200 transition-colors"
                          title="Edit additional leads"
                        >
                          <PencilIcon className="h-3 w-3 text-gray-400" />
                        </button>
                      )}
                    </div>
                    <Typography variant="small" className="font-bold">{totalLeads}</Typography>
                    <div className="text-[10px] text-gray-400">
                      {controllerEmails} emails · {controllerCalls} calls · {controllerTexts} texts
                      {hasCustom && <span className="text-green-600"> + {customTotal} additional</span>}
                    </div>
                    {cl.note && <div className="text-[10px] text-gray-400 italic">{cl.note}</div>}
                  </div>
                  {onToggleKpi && <KpiToggle id="totalLeads" />}
                </div>
              );
            })()}
          </div>
          {/* Custom Leads Editor */}
          {showLeadsEditor && onSaveCustomLeads && (
            <div className="mt-2 p-3 rounded-lg border border-green-200 bg-green-50">
              <Typography variant="small" className="font-medium text-xs text-green-800 mb-2">
                Additional Leads (not from Controller)
              </Typography>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {['calls', 'emails', 'texts', 'other'].map((field) => (
                  <div key={field}>
                    <label className="text-[10px] text-gray-500 capitalize">{field}</label>
                    <input
                      type="number"
                      min="0"
                      value={customLeadsForm[field]}
                      onChange={(e) => setCustomLeadsForm((prev) => ({ ...prev, [field]: parseInt(e.target.value, 10) || 0 }))}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-green-500"
                    />
                  </div>
                ))}
              </div>
              <input
                type="text"
                value={customLeadsForm.note}
                onChange={(e) => setCustomLeadsForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Note (optional)"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-green-500 mb-2"
              />
              <div className="flex justify-end gap-2">
                {content.customLeads && (
                  <Button variant="text" size="sm" className="py-0.5 px-2 text-[10px]" onClick={() => { onSaveCustomLeads(null); setShowLeadsEditor(false); }}>
                    Clear
                  </Button>
                )}
                <Button variant="text" size="sm" className="py-0.5 px-2 text-[10px]" onClick={() => setShowLeadsEditor(false)}>
                  Cancel
                </Button>
                <Button size="sm" className="py-0.5 px-2 text-[10px]" onClick={() => {
                  const hasValues = customLeadsForm.calls || customLeadsForm.emails || customLeadsForm.texts || customLeadsForm.other;
                  onSaveCustomLeads(hasValues ? customLeadsForm : null);
                  setShowLeadsEditor(false);
                }}>
                  Save
                </Button>
              </div>
            </div>
          )}
          {/* Peer Benchmark Bar */}
          {content.peerBenchmark && (
            <div className={`mt-2 p-2 rounded-lg border transition-opacity ${isKpiHidden('peerBenchmark') ? 'opacity-40 bg-gray-50' : 'bg-indigo-50 border-indigo-200'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <Typography variant="small" className="font-medium text-xs text-indigo-700">
                  Benchmark — {content.peerBenchmark.model} ({content.peerBenchmark.sampleSize} listings · {content.peerBenchmark.periodLabel || `Last ${content.peerBenchmark.periodDays}d`})
                </Typography>
                {onToggleKpi && <KpiToggle id="peerBenchmark" />}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
                {(() => {
                  const bm = content.peerBenchmark;
                  // Views/day — median with range, backwards compat from PerWeek
                  const medianViews = bm.medianViewsPerDay ?? (bm.medianViewsPerWeek != null ? bm.medianViewsPerWeek / 7 : null);
                  const p25Views = bm.p25ViewsPerDay ?? (bm.p25ViewsPerWeek != null ? bm.p25ViewsPerWeek / 7 : null);
                  const p75Views = bm.p75ViewsPerDay ?? (bm.p75ViewsPerWeek != null ? bm.p75ViewsPerWeek / 7 : null);
                  const avgViews = bm.avgViewsPerDay ?? (bm.avgViewsPerWeek != null ? bm.avgViewsPerWeek / 7 : null);
                  const viewsDisplay = medianViews != null
                    ? `${medianViews.toFixed(1)} (${(p25Views || 0).toFixed(1)}–${(p75Views || 0).toFixed(1)})`
                    : avgViews != null ? avgViews.toFixed(1) : '—';
                  // Impressions/day — median when available (parity with views), avg for old drafts
                  const imprPerDay = bm.medianImpressionsPerDay ?? bm.avgImpressionsPerDay ?? (bm.avgImpressionsPerWeek != null ? bm.avgImpressionsPerWeek / 7 : null);
                  // Recent 4wk — median, backwards compat
                  const last4wMedian = bm.last4wMedianViewsPerDay ?? (bm.last4wMedianViewsPerWeek != null ? bm.last4wMedianViewsPerWeek / 7 : null);
                  const last4wAvg = bm.last4wAvgViewsPerDay ?? (bm.last4wAvgViewsPerWeek != null ? bm.last4wAvgViewsPerWeek / 7 : null);
                  const last4wValue = last4wMedian ?? last4wAvg;
                  // Watchlists/day
                  const watchPerDay = bm.avgDaysAging > 0 ? bm.avgWatchlists / bm.avgDaysAging : 0;
                  return [
                    { kpiId: 'totalViews', label: 'Views/day', value: viewsDisplay },
                    { kpiId: 'totalImpressions', label: 'Impr./day', value: imprPerDay != null ? fmtNum(imprPerDay) : '—' },
                    ...(last4wValue != null ? [{ kpiId: 'recentTrend', label: `Recent 4wk/day (${bm.last4wSampleSize || '—'} peers)`, value: last4wValue.toFixed(1) }] : []),
                    { kpiId: 'overallCtr', label: 'Avg CTR', value: fmtPct(bm.avgCtr) },
                    { kpiId: 'daysAging', label: 'Avg DOM', value: bm.avgDaysAging != null ? Math.round(bm.avgDaysAging) + 'd' : '—' },
                    { kpiId: 'totalWatchlists', label: 'Watchlists/day', value: watchPerDay.toFixed(2) },
                  ];
                })().map((m) => (
                  <div key={m.label} className={`text-center relative transition-opacity ${isKpiHidden(m.kpiId) ? 'opacity-40' : ''}`}>
                    <div className="text-[10px] text-indigo-500">{m.label}</div>
                    <div className="text-xs font-semibold text-indigo-800">{m.value}</div>
                    {onToggleKpi && (
                      <div className="absolute -top-1 -right-1">
                        <KpiToggle id={m.kpiId} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Callout cards */}
          <div className="flex flex-wrap gap-2 mt-2">
            {CALLOUT_CONFIG.map((callout) => {
              if (!callout.show(content)) return null;
              const hidden = isKpiHidden(callout.id);
              if (callout.id === 'viewsTrend') {
                return (
                  <div key={callout.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-center transition-opacity ${hidden ? 'opacity-40 bg-gray-50' : trendColor}`}>
                    <div>
                      <div className="text-xs uppercase tracking-wide">{callout.label}</div>
                      <div className="text-sm font-semibold">{(content.viewsTrendPct ?? 0) > 0 ? '+' : ''}{(content.viewsTrendPct ?? 0).toFixed(0)}%</div>
                    </div>
                    {onToggleKpi && <KpiToggle id={callout.id} />}
                  </div>
                );
              }
              if (callout.id === 'peakViews') {
                return (
                  <div key={callout.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-gray-50 text-center transition-opacity ${hidden ? 'opacity-40' : ''}`}>
                    <div>
                      <div className="text-xs text-gray-500">{callout.label}</div>
                      <div className="text-sm font-semibold">{fmtNum(content.peakViewsCount)}</div>
                    </div>
                    {onToggleKpi && <KpiToggle id={callout.id} />}
                  </div>
                );
              }
              if (callout.id === 'featuredImpact') {
                return (
                  <div key={callout.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-center transition-opacity ${hidden ? 'opacity-40 bg-gray-50' : content.featuredLiftPct > 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                    <div>
                      <div className="text-xs uppercase tracking-wide">{callout.label}</div>
                      <div className="text-sm font-semibold">{content.featuredLiftPct > 0 ? '+' : ''}{content.featuredLiftPct.toFixed(0)}%</div>
                    </div>
                    {onToggleKpi && <KpiToggle id={callout.id} />}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </CardBody>
      </Card>

      {/* Commentary */}
      <Card className="border border-blue-200">
        <CardBody className="p-2">
          <Typography variant="small" className="font-medium text-xs mb-1 text-blue-800">
            Performance Analysis
          </Typography>
          <Textarea
            value={editedCommentary}
            onChange={(e) => setEditedCommentary(e.target.value)}
            rows={5}
            className="mb-2 text-xs"
            label="Commentary"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outlined" size="sm" onClick={handleReset} disabled={!hasChanges || isSaving} className="py-1 px-2 text-xs">
              Reset
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving} className="py-1 px-2 text-xs">
              {isSaving ? <><Spinner className="h-3 w-3 mr-1" />Saving...</> : 'Save'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Chart Visibility */}
      {onToggleChart && (
        <Card className="border border-gray-200">
          <CardBody className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <EyeIcon className="h-4 w-4 text-gray-500" />
              <Typography variant="small" className="font-medium text-xs text-gray-700">
                Chart Visibility in Report
              </Typography>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {CHART_CONFIG.filter((cfg) => !cfg.defaultHidden || content.charts?.[cfg.prop]).map((cfg) => {
                const isVisible = cfg.defaultHidden ? hiddenCharts.includes(cfg.id) : !hiddenCharts.includes(cfg.id);
                return (
                  <div key={cfg.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded" onClick={() => onToggleChart(cfg.id)}>
                    <Checkbox checked={isVisible} onChange={() => {}} className="h-4 w-4 pointer-events-none" containerProps={{ className: 'p-0' }} />
                    <Typography variant="small" className="text-xs">{cfg.label}</Typography>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

    </div>
  );
}

export default ListingPerformanceSection;
